Component for working with the active presentation session
<template>
	<div class="h-full w-full">
		<router-view
			v-if="sessionStore.activeSession"
			v-slot="{ Component }"
		>
			<component :is="Component" />
		</router-view>
	</div>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import type { Session } from '@/classes/Session'
import { initializePlanOap } from '@/mixins/oapPlanProvider'
import { watchThrottledIgnoringKeys } from '@/mixins/watchThrottledIgnoringKeys'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import { watchArray, watchThrottled } from '@vueuse/core'
import { onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

const sessionStore = useSessionStore()
const planStore = usePlanStore()
const router = useRouter()

const props = defineProps({
	sessionId: String,
})

/** Callbacks to remove watchers for individual plans */
let planWatchers: (() => void)[] = []

/** Stop the session save watcher on unmount */
let stopSessionWatcher: (() => void) | undefined

/**
 * Set up watchers for all plans in the array.
 * Called automatically whenever the plans array changes.
 */
function setupPlanWatchers(newPlans: Plan[]) {
	// Clear existing watchers
	planWatchers.forEach((unwatch) => unwatch())
	planWatchers = []

	newPlans.forEach((plan) => {
		// Watch payment mode, reset payment-related document progress
		planWatchers.push(
			watchThrottled(
				() => ({
					paymentType: plan.insurance.paymentType,
					paymentFrequency: plan.insurance.paymentFrequency,
					term: plan.insurance.term,
				}),
				(newInsurance, oldInsurance) => {
					if (JSON.stringify(newInsurance) !== JSON.stringify(oldInsurance)) {
						planStore.resetDocumentFlowForPaymentChange(plan)
						planStore.resetPaymentTypeAndDetails(plan)
					}
				},
				{ deep: true, throttle: 100 }
			)
		)

		// Watch SSN and update secret
		planWatchers.push(
			watchThrottled(
				() => plan.socialSecurityNumber,
				() => {
					planStore.updateSecret(plan)
				},
				{ throttle: 100 }
			)
		)

		planWatchers.push(
			watchThrottledIgnoringKeys<Plan, 'lastUpdated'>(
				() => plan,
				['lastUpdated'],
				async (p) => {
					p.lastUpdated = new Date().toISOString()
					await planStore.savePlan(p)
				},
				{ throttle: 1000 }
			)
		)
	})
}

onMounted(async () => {
	// Try to load the session that the URL is asking for
	const savedSessions = await sessionStore.getSavedSessionIds()

	if (!props.sessionId) {
		// Not sure this branch is possible
		router.push({ name: 'new-session' })
		return
	}

	if (savedSessions.includes(props.sessionId)) {
		// Load the saved session
		await sessionStore.loadSession(props.sessionId)

		// Save the session whenever it is updated (watch excludes lastUpdated to avoid loop)
		stopSessionWatcher = watchThrottledIgnoringKeys<Session, 'lastUpdated'>(
			() => sessionStore.activeSession,
			['lastUpdated'],
			async (session) => {
				session.lastUpdated = new Date().toISOString()
				await sessionStore.saveActiveSession()
			},
			{ throttle: 1000 }
		)

		// Watch the plans array and update watchers when it changes
		watchArray(
			() => sessionStore.activeSession?.planIds ?? [],
			async (newValue) => {
				await planStore.setActivePlanIds(newValue)
				setupPlanWatchers(planStore.plans)
			},
			{ immediate: false, deep: true }
		)

		// Start watching the current plans
		setupPlanWatchers(planStore.plans)

		planStore.plans.forEach(initializePlanOap)
	} else {
		throw new Error(
			"URL is requesting a session we don't have in storage: " + props.sessionId
		)
	}
})

onUnmounted(() => {
	stopSessionWatcher?.()

	sessionStore.activeSession = undefined

	planWatchers.forEach((unwatch) => unwatch())
})
</script>
